import * as React from "react";
import { Briefcase, Check, Info, CalendarCheck } from "lucide-react";

import { benefitGroups } from "@/components/admin/profile-editor/sections/BenefitsSection";
import { selectedBenefitSet } from "@/lib/orgBenefits";
import { modelLabel } from "@/lib/compensationModelLabels";
import { feeTypeLabel } from "@/lib/feeTypeLabels";
import { cn } from "@/lib/utils";
import { SectionHead, Tag, TagRow, Stat, EmptyTab, hasStatValue } from "@/components/organization/kit";
import DiscoveryCard from "@/components/organization/kit/DiscoveryCard";
import SurfaceCard from "@/components/primitives/SurfaceCard";
import CareerPath from "@/components/organization/value/CareerPath";
import OnboardingPlan from "@/components/organization/value/OnboardingPlan";
import { normalizeOnboardingStages, type OnboardingStage } from "@/lib/onboardingPlan";
import { normalizeSupport, type NormalizedSupport } from "@/lib/orgSupport";
import { looksLikePricing } from "@/lib/noPricing";
import { resolveLeadGenerationAnswer, leadGenerationLabel, type LeadGenerationAnswer } from "@/lib/orgLeadGeneration";

/** The blocks this component renders, in render order. See `parts`. */
export type ValuePart = "comp" | "fees" | "leads" | "growth" | "training" | "support" | "onboarding" | "benefits";

interface ProfileDataValuePropProps {
  profileData: Record<string, any>;
  orgName?: string;
  orgLogo?: string;
  /** The single shared book-a-discovery-call action — the same handler the header uses. */
  onBookCall?: () => void;
  /**
   * Render only these blocks (agent story page, spec 2026-09-24 §5.1: Offer,
   * Growth and "How it runs" each take a subset). With `parts`, the page
   * padding and the whole-tab empty card are dropped and the component renders
   * nothing when none of its parts has content. Omit for the classic Value tab.
   */
  parts?: readonly ValuePart[];
}

/** The capping-model keys — both spellings gate the same cap prose. */
const CAP_MODEL_KEYS = ["capping_model", "cap_based"];

/** A small uppercase sub-label inside a present section. */
const SubLabel = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div className={`eyebrow text-ink-500 ${className ?? "mb-2.5"}`}>{children}</div>
);

/** A gold-free teal-check availability row (training / support items). */
const FeatureRow = ({
  label,
  sub,
  aside,
}: {
  label: React.ReactNode;
  sub?: string;
  aside?: React.ReactNode;
}) => (
  <div className="flex items-start gap-2.5">
    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2.4} />
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-ink-900">{label}</span>
        {aside}
      </div>
      {sub && <p className="mt-0.5 text-[13px] text-ink-500">{sub}</p>}
    </div>
  </div>
);

/**
 * Everything ProfileDataValueProp needs — both the display data it renders
 * with and the has-content flags that decide what shows — computed once from
 * profileData. Exported so a caller that only needs the presence decision
 * (the org story page's section predicates, `sections.tsx`) can share this
 * exact logic instead of re-deriving it and risking drift. See
 * `valuePartsWithContent` below for the actual show/hide gate, which adds the
 * `parts` filter and the compensation card's `onBookCall` fallback on top of
 * these flags.
 */
export interface ValuePropModel {
  compModels: string[];
  compensationPhilosophy: string | null;
  capDescription: string | null;
  hasComp: boolean;
  primaryModelLabel: string | null;

  feeTypesArr: string[];
  noRecurring: boolean;
  feeNotes: string | null;
  hasFees: boolean;

  leadSourceTags: string[];
  leadsPerMonth: React.ReactNode;
  conversion: string | null;
  hasLeadsPerMonth: boolean;
  hasConversion: boolean;
  leadAnswer: LeadGenerationAnswer | null;
  leadProgramDetails: string | null;
  hasLeads: boolean;

  careerSteps: string[];
  timeline: string | null;
  ownership: string | null;
  hasGrowth: boolean;
  hasOwnership: boolean;

  trainingPrograms: string[];
  hasTraining: boolean;
  hasTrainingBody: boolean;

  onboardingStages: OnboardingStage[];
  hasOnboarding: boolean;

  marketingSupport: string[];
  support: NormalizedSupport;
  hasSupport: boolean;

  benefitGroupsToShow: { title: string; items: { label: string; icon?: unknown }[] }[];
  hasBenefits: boolean;
}

export function computeValuePropModel(profileData: Record<string, any>): ValuePropModel {
  const p = profileData || {};

  // ── Compensation ── categorical model labels only; numbers are never shown.
  const compModels: string[] = p.compensationModels || [];
  // Free-text compensation prose (never numbers — the editor guidance steers orgs
  // toward describing their approach rather than percentages or dollar figures).
  const compensationPhilosophy: string | null = p.compensationPhilosophy || null;
  const capDescription: string | null = p.capDescription || null;
  // Prose counts as content too — an org that wrote a message but selected no model
  // pills still has a compensation story to show (and must not fall to the DiscoveryCard).
  const hasComp = compModels.length > 0 || !!compensationPhilosophy;
  // Reserved primary-compensation-model slot: a single normalized label, or omit.
  const primaryModelLabel: string | null = p.primaryCompensationModel
    ? modelLabel(p.primaryCompensationModel) || null
    : null;

  // ── Fees & costs ──
  const feeTypesArr: string[] = p.feeTypes || [];
  const noRecurring = !!p.noRecurringFees;
  const feeNotes: string | null = p.feeNotes || null;
  const hasFees = noRecurring || feeTypesArr.length > 0 || !!feeNotes;

  // ── Lead generation ──
  const structuredSources: { name?: string }[] = p.leadSourcesStructured || [];
  const legacySources: string[] = p.leadSources || [];
  const leadSourceTags: string[] = (structuredSources.length > 0
    ? structuredSources.map((s) => s.name)
    : legacySources
  ).filter((t): t is string => typeof t === "string" && t.trim() !== "");
  // `||`, not `??`: the editor seeds both keys as "", and "" is not nullish, so
  // `??` handed the empty string through as if it were a published figure.
  const leadsPerMonth = p.leadsPerMonth || p.leadVolume || null;
  const conversion = p.conversionRate != null && p.conversionRate !== "" ? `${p.conversionRate}%` : null;
  const hasLeadsPerMonth = hasStatValue(leadsPerMonth);
  const hasConversion = hasStatValue(conversion);
  // An unanswered lead-generation question renders no chip and makes no claim,
  // and when nothing else about leads was published the card is absent too.
  // It used to render as an empty "Lead generation / Leads and support" shell,
  // because the editor's blank strings counted as content (founder re-review,
  // 2026-09-18).
  const leadAnswer = resolveLeadGenerationAnswer(p);
  // The editor's "Lead program details" (#958 PR 4). It belongs to the
  // "we provide leads" answer only, as in the editor, which hides the field
  // otherwise; text left over from an earlier answer never shows. Priced text
  // never shows either. Signed-in only: the public RPC does not project it.
  const rawLeadDetails = typeof p.leadProgramDetails === "string" ? p.leadProgramDetails.trim() : "";
  const leadProgramDetails: string | null =
    leadAnswer === "provided" && rawLeadDetails !== "" && !looksLikePricing(rawLeadDetails) ? rawLeadDetails : null;
  const hasLeads = leadAnswer != null || hasLeadsPerMonth || hasConversion || leadSourceTags.length > 0
    || leadProgramDetails !== null;

  // ── Growth & advancement ──
  const careerSteps: string[] = (p.careerStages || []).map((s: any) => s?.title).filter(Boolean);
  const timeline: string | null = p.careerPathDescription || null;
  const ownership: string | null = p.partnershipDetails || null;
  const hasGrowth = careerSteps.length > 0 || !!timeline || !!p.partnershipOpportunities || !!ownership;
  const hasOwnership = !!ownership || !!p.partnershipOpportunities;

  // ── Training & coaching (kept card) ──
  const trainingPrograms: string[] = (p.trainingPrograms || []).map((t: any) => t?.name).filter(Boolean);
  const hasTraining = !!p.trainingFrequency || !!p.oneOnOneCoaching || !!p.hasTrainingLibrary
    || trainingPrograms.length > 0;
  const hasTrainingBody = trainingPrograms.length > 0 || !!p.oneOnOneCoaching || !!p.hasTrainingLibrary;

  // ── Onboarding plan — its own full-width card, one normalizer shared with the public page ──
  const onboardingStages = normalizeOnboardingStages(p.onboardingStages);
  const hasOnboarding = onboardingStages.length > 0;

  // ── Support & services (kept card) ── through the shared reader, so priced text never shows.
  const support = normalizeSupport(p);
  const marketingSupport: string[] = support.marketingSupport;
  const hasSupport = !support.isEmpty;

  // ── Benefits & perks (kept card) ── merge legacy flags into the selected set, then group.
  const finalSelected = Array.from(selectedBenefitSet(p));
  const benefitGroupsToShow = benefitGroups
    .map((group) => ({ ...group, items: group.items.filter((item) => finalSelected.includes(item.label)) }))
    .filter((group) => group.items.length > 0);
  const hasBenefits = finalSelected.length > 0 || !!p.ptoPolicy;

  return {
    compModels,
    compensationPhilosophy,
    capDescription,
    hasComp,
    primaryModelLabel,
    feeTypesArr,
    noRecurring,
    feeNotes,
    hasFees,
    leadSourceTags,
    leadsPerMonth,
    conversion,
    hasLeadsPerMonth,
    hasConversion,
    leadAnswer,
    leadProgramDetails,
    hasLeads,
    careerSteps,
    timeline,
    ownership,
    hasGrowth,
    hasOwnership,
    trainingPrograms,
    hasTraining,
    hasTrainingBody,
    onboardingStages,
    hasOnboarding,
    marketingSupport,
    support,
    hasSupport,
    benefitGroupsToShow,
    hasBenefits,
  };
}

/**
 * Which of the eight `ValuePart` blocks have content, given an already-built
 * `ValuePropModel` plus an optional `parts` filter and `onBookCall` — mirrors
 * the component's own `want()`/`show*` gating exactly, including the
 * compensation card's fallback-to-DiscoveryCard rule
 * (`!parts || hasComp || !!onBookCall`).
 *
 * Internal: takes the model rather than raw `profileData` so the component
 * below can build the model exactly once per render (memoized) and reuse it
 * here, instead of `valuePartsWithContent` recomputing it — see the
 * component's own `React.useMemo(() => computeValuePropModel(profileData), …)`
 * comment below for why a second computation is more than a wasted cycle for
 * stages without a persisted id. That memoization is keyed on the
 * `profileData` reference, so any caller building `profileData` inline on
 * every render (e.g. spreading into a fresh object each time) defeats it —
 * pass a stable reference.
 */
function visiblePartsFromModel(
  model: ValuePropModel,
  { parts, onBookCall }: { parts?: readonly ValuePart[]; onBookCall?: () => void } = {},
): ValuePart[] {
  const want = (part: ValuePart) => !parts || parts.includes(part);

  // `parts` gate (agent story page). The classic tab passes no parts, so every
  // show* flag equals its has* flag and nothing here changes for it.
  // Compensation always renders on the classic tab (a DiscoveryCard stands in
  // when nothing is shared). In a story section it renders only with content
  // or with a call to book — never an empty policy card.
  const showComp = want("comp") && (!parts || model.hasComp || !!onBookCall);
  const showFees = want("fees") && model.hasFees;
  const showLeads = want("leads") && model.hasLeads;
  const showGrowth = want("growth") && model.hasGrowth;
  const showTraining = want("training") && model.hasTraining;
  const showSupport = want("support") && model.hasSupport;
  const showOnboarding = want("onboarding") && model.hasOnboarding;
  const showBenefits = want("benefits") && model.hasBenefits;

  const result: ValuePart[] = [];
  if (showComp) result.push("comp");
  if (showFees) result.push("fees");
  if (showLeads) result.push("leads");
  if (showGrowth) result.push("growth");
  if (showTraining) result.push("training");
  if (showSupport) result.push("support");
  if (showOnboarding) result.push("onboarding");
  if (showBenefits) result.push("benefits");
  return result;
}

/**
 * Which of the eight `ValuePart` blocks currently have content to show, given
 * raw `profileData`, an optional `parts` filter and `onBookCall`.
 *
 * This is the single source of truth for "would this block render" — the
 * component below builds its `show*` booleans from the same
 * `visiblePartsFromModel` helper (over its own memoized model), and so does
 * the org story page's `storySectionHasContent` (`sections.tsx`), so all
 * three can never drift into disagreeing about whether a section has
 * content. A caller with only `profileData` (not already holding a built
 * model) uses this entry point; it builds the model once for the call.
 */
export function valuePartsWithContent(
  profileData: Record<string, any>,
  opts: { parts?: readonly ValuePart[]; onBookCall?: () => void } = {},
): ValuePart[] {
  return visiblePartsFromModel(computeValuePropModel(profileData), opts);
}

/** True when at least one of the named blocks (or, with no `parts`, any of the eight) would show. */
export function valuePropHasContent(
  profileData: Record<string, any>,
  parts?: readonly ValuePart[],
  onBookCall?: () => void,
): boolean {
  return valuePartsWithContent(profileData, { parts, onBookCall }).length > 0;
}

export default function ProfileDataValueProp({ profileData, orgName, onBookCall, parts }: ProfileDataValuePropProps) {
  const p = profileData || {};
  // Memoized on the profileData reference. normalizeOnboardingStages (called
  // from computeValuePropModel) assigns a FRESH random id to any legacy
  // onboarding stage that has none (see @/lib/onboardingPlan's newStageId),
  // and OnboardingPlan keys its rendered stages on that id — recomputing the
  // model on every render therefore minted a new id, and a new React key, for
  // every id-less stage on every render, remounting it each time. Building
  // the model once per profileData reference keeps those ids (and the DOM
  // nodes keyed on them) stable across re-renders that don't actually change
  // the data.
  const model = React.useMemo(() => computeValuePropModel(profileData), [profileData]);
  const {
    compModels,
    compensationPhilosophy,
    capDescription,
    hasComp,
    primaryModelLabel,
    feeTypesArr,
    noRecurring,
    feeNotes,
    hasFees,
    leadSourceTags,
    leadsPerMonth,
    conversion,
    hasLeadsPerMonth,
    hasConversion,
    leadAnswer,
    leadProgramDetails,
    hasLeads,
    careerSteps,
    timeline,
    ownership,
    hasGrowth,
    hasOwnership,
    trainingPrograms,
    hasTraining,
    hasTrainingBody,
    onboardingStages,
    hasOnboarding,
    marketingSupport,
    support,
    hasSupport,
    benefitGroupsToShow,
    hasBenefits,
  } = model;

  const visibleParts = new Set(visiblePartsFromModel(model, { parts, onBookCall }));
  const showComp = visibleParts.has("comp");
  const showFees = visibleParts.has("fees");
  const showLeads = visibleParts.has("leads");
  const showGrowth = visibleParts.has("growth");
  const showTraining = visibleParts.has("training");
  const showSupport = visibleParts.has("support");
  const showOnboarding = visibleParts.has("onboarding");
  const showBenefits = visibleParts.has("benefits");
  if (parts && visibleParts.size === 0) {
    return null;
  }

  // Whole-tab empty — only when EVERY section (the four spec sections, the three
  // kept cards and the onboarding plan) is absent, so a filled card is never buried under it.
  const allAbsent = !hasComp && !hasFees && !hasLeads && !hasGrowth && !hasTraining && !hasOnboarding && !hasSupport && !hasBenefits;
  if (!parts && allAbsent) {
    return (
      <div className="py-8">
        <EmptyTab
          icon={Briefcase}
          title="Compensation and growth are shared directly"
          body={`${orgName || "This organization"} keeps the specifics of pay, fees, and advancement for a conversation. Compaire shares the model up front and the math in person.`}
          cta={onBookCall ? "Book a discovery call" : undefined}
          onCta={onBookCall}
        />
      </div>
    );
  }

  return (
    <div className={cn("space-y-[22px]", !parts && "py-8")}>
      {/* ── COMPENSATION — model labels only; numbers never shown ── */}
      {showComp && (
        <SurfaceCard padding="lg" className="rounded-2xl">
          <SectionHead kicker="Compensation" title="How agents are paid" />
          {primaryModelLabel && (
            <div className="mb-4">
              <SubLabel>Primary model</SubLabel>
              <Tag tone="teal">{primaryModelLabel}</Tag>
            </div>
          )}
          {hasComp ? (
            <div className="flex flex-col gap-[18px]">
              {compModels.length > 0 && (
                <TagRow tone="teal" tags={compModels.map(modelLabel).filter(Boolean)} />
              )}
              {compensationPhilosophy && (
                <p className="m-0 text-[14.5px] leading-[1.6] text-ink-700">{compensationPhilosophy}</p>
              )}
              {/* Cap prose only alongside a selected capping model — deselecting the model
                  does not clear capDescription in the editor, so this guards orphaned text. */}
              {capDescription && compModels.some((k) => CAP_MODEL_KEYS.includes(k)) && (
                <div>
                  <SubLabel>When you cap</SubLabel>
                  <p className="m-0 text-[14.5px] leading-[1.6] text-ink-700">{capDescription}</p>
                </div>
              )}
              <div className="flex items-start gap-2.5 rounded-[var(--radius-md)] border border-border bg-background px-4 py-[13px]">
                <Info className="mt-px h-[17px] w-[17px] shrink-0 text-ink-500" strokeWidth={1.9} />
                <p className="m-0 text-[13.5px] leading-[1.55] text-ink-600">
                  Compaire shares the <strong className="font-semibold text-ink-900">model</strong>, not the math.
                  Splits, caps, and dollar figures are discussed directly with the team — never posted on the profile.
                </p>
              </div>
            </div>
          ) : (
            <DiscoveryCard
              // Without a scheduling link the card must not promise a call; it
              // states the policy and stops (founder re-review, 2026-09-18).
              title={onBookCall ? "Compensation is shared on a discovery call" : "Compensation is shared directly"}
              body={
                onBookCall
                  ? `${orgName || "This organization"} keeps its compensation structure for a direct conversation. You'll get the full picture — the model, the numbers, and what it means for your business — instead of a figure on a public page.`
                  : `${orgName || "This organization"} keeps its compensation structure for a direct conversation with the team rather than a figure on a public page.`
              }
              note="Compaire deliberately shares the compensation model, not raw percentages or dollar figures."
              onBookCall={onBookCall}
            />
          )}
        </SurfaceCard>
      )}

      {/* ── FEES & COSTS · LEAD GENERATION — each card only when it has content ── */}
      {(showFees || showLeads) && (
        <div className={cn("grid gap-[22px]", showFees && showLeads && "lg:grid-cols-2")}>
          {showFees && (
            <SurfaceCard padding="lg" className="rounded-2xl">
              <SectionHead kicker="Fees and costs" title="What you would pay" />
              <div className="flex flex-col gap-[18px]">
                {noRecurring ? (
                  <div className="flex items-center gap-3 rounded-[var(--radius-md)] border border-primary bg-primary-tint px-[18px] py-4">
                    <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-primary">
                      <Check className="h-[18px] w-[18px] text-primary-foreground" strokeWidth={2.4} />
                    </div>
                    <div>
                      <div className="text-[14.5px] font-semibold text-primary-pressed">No recurring fees</div>
                      <div className="mt-0.5 text-[13px] text-ink-600">No desk, technology, or monthly fees.</div>
                    </div>
                  </div>
                ) : feeTypesArr.length > 0 ? (
                  <TagRow tags={feeTypesArr.map(feeTypeLabel).filter(Boolean)} />
                ) : null}
                {feeNotes && <p className="m-0 text-[14.5px] leading-[1.6] text-ink-700">{feeNotes}</p>}
              </div>
            </SurfaceCard>
          )}

          {showLeads && (
            <SurfaceCard padding="lg" className="rounded-2xl">
              <SectionHead kicker="Lead generation" title="Leads and support" />
              <div className="flex flex-col gap-4">
                {leadAnswer && (
                  <span className="inline-flex items-center gap-2 self-start rounded-full border border-primary bg-primary-tint px-3 py-[5px] text-[13.5px] font-semibold text-primary-pressed">
                    <Check className="h-3.5 w-3.5 text-primary" strokeWidth={2.4} />
                    {leadGenerationLabel(leadAnswer)}
                  </span>
                )}
                {leadSourceTags.length > 0 && (
                  <div>
                    <SubLabel>Sources</SubLabel>
                    <TagRow tags={leadSourceTags} />
                  </div>
                )}
                {leadProgramDetails && (
                  <p className="m-0 text-[14.5px] leading-[1.6] text-ink-700">{leadProgramDetails}</p>
                )}
                {(hasLeadsPerMonth || hasConversion) && (
                  <div
                    className={cn(
                      "grid overflow-hidden rounded-[var(--radius-md)] border border-border",
                      hasLeadsPerMonth && hasConversion ? "grid-cols-2" : "grid-cols-1",
                    )}
                  >
                    {hasLeadsPerMonth && (
                      <div className="bg-card px-4 py-3.5">
                        <Stat label="Leads / mo" value={leadsPerMonth} />
                      </div>
                    )}
                    {hasConversion && (
                      <div className={cn("bg-card px-4 py-3.5", hasLeadsPerMonth && "border-l border-border")}>
                        <Stat label="Conversion" value={conversion} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </SurfaceCard>
          )}
        </div>
      )}

      {/* ── GROWTH & ADVANCEMENT — sub-blocks only when present ── */}
      {showGrowth && (
        <SurfaceCard padding="lg" className="rounded-2xl">
          <SectionHead kicker="Growth and advancement" title="The path forward" />
          <div className="flex flex-col gap-[22px]">
            {careerSteps.length > 0 && (
              <div>
                <SubLabel className="mb-[11px]">Career path</SubLabel>
                <CareerPath steps={careerSteps} />
              </div>
            )}
            {(timeline || hasOwnership) && (
              <div className={cn("grid gap-[22px]", timeline && hasOwnership && "sm:grid-cols-2")}>
                {timeline && (
                  <div>
                    <SubLabel className="mb-2">Advancement timeline</SubLabel>
                    <p className="m-0 text-[14.5px] leading-[1.6] text-ink-700">{timeline}</p>
                  </div>
                )}
                {hasOwnership && (
                  <div>
                    <SubLabel className="mb-2">Partnership and ownership</SubLabel>
                    <p className="m-0 text-[14.5px] leading-[1.6] text-ink-700">
                      {ownership || "Partnership opportunities available."}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </SurfaceCard>
      )}

      {/* ── TRAINING · SUPPORT — each card only when it has content ── */}
      {(showTraining || showSupport) && (
        <div className={cn("grid gap-[22px]", showTraining && showSupport && "lg:grid-cols-2")}>
          {showTraining && (
            <SurfaceCard padding="lg" className="rounded-2xl">
              <SectionHead
                kicker="Training"
                title="Training and coaching"
                aside={p.trainingFrequency && hasTrainingBody ? <Tag tone="teal">{p.trainingFrequency}</Tag> : undefined}
              />
              <div className="flex flex-col gap-4">
                {p.trainingFrequency && !hasTrainingBody && (
                  <div>
                    <SubLabel>Cadence</SubLabel>
                    <TagRow
                      tone="teal"
                      tags={String(p.trainingFrequency).split(",").map((s) => s.trim()).filter(Boolean)}
                    />
                  </div>
                )}
                {trainingPrograms.length > 0 && (
                  <div>
                    <SubLabel>Training programs</SubLabel>
                    <TagRow tags={trainingPrograms} />
                  </div>
                )}
                {(p.oneOnOneCoaching || p.hasTrainingLibrary) && (
                  <div className="flex flex-col gap-2.5">
                    {p.oneOnOneCoaching && (
                      <FeatureRow
                        label="1-on-1 coaching available"
                        aside={p.coachingFrequency ? <Tag>{p.coachingFrequency}</Tag> : undefined}
                      />
                    )}
                    {p.hasTrainingLibrary && <FeatureRow label="On-demand training library" />}
                  </div>
                )}
              </div>
            </SurfaceCard>
          )}

          {showSupport && (
            <SurfaceCard padding="lg" className="rounded-2xl">
              <SectionHead kicker="Support" title="Support and services" />
              <div className="flex flex-col gap-2.5">
                {support.transactionCoordinator && (
                  <FeatureRow label="Transaction coordinator" sub={support.tcDetails ?? undefined} />
                )}
                {support.adminAssistance && (
                  <FeatureRow label="Admin assistance" sub={support.adminDetails ?? undefined} />
                )}
                {support.marketingSupport.map((m, i) => (
                  <FeatureRow key={i} label={m} />
                ))}
              </div>
            </SurfaceCard>
          )}
        </div>
      )}

      {/* ── ONBOARDING PLAN — full plan, shared renderer with the public page ── */}
      {showOnboarding && <OnboardingPlan stages={onboardingStages} />}

      {/* ── BENEFITS & PERKS — only when present ── */}
      {showBenefits && (
        <SurfaceCard padding="lg" className="rounded-2xl">
          <SectionHead kicker="Benefits" title="Benefits and perks" />
          <div className="flex flex-col gap-5">
            {benefitGroupsToShow.map((group) => (
              <div key={group.title}>
                <SubLabel>{group.title}</SubLabel>
                <div className="flex flex-col gap-2.5">
                  {group.items.map((item) => (
                    <div key={item.label} className="flex items-center gap-2.5 text-sm text-ink-700">
                      <Check className="h-4 w-4 shrink-0 text-primary" strokeWidth={2.2} />
                      {item.label}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {p.ptoPolicy && (
              <div className="flex items-start gap-3 rounded-[var(--radius-md)] border border-primary/20 bg-primary-tint p-4">
                <CalendarCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <p className="text-sm font-semibold text-ink-900">PTO policy</p>
                  <p className="mt-0.5 text-sm text-ink-700">{p.ptoPolicy}</p>
                </div>
              </div>
            )}
          </div>
        </SurfaceCard>
      )}
    </div>
  );
}
